import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, PlayCircle, RefreshCw, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface BuildProgress {
  status: 'initializing' | 'rendering' | 'uploading' | 'completed' | 'failed';
  currentModule: number;
  totalModules: number;
  currentCourse?: string;
  successCount: number;
  failedCount: number;
  errors: string[];
  message: string;
}

export default function SSGBuilder() {
  const [building, setBuilding] = useState(false);
  const [progress, setProgress] = useState<BuildProgress | null>(null);
  const [buildHistory, setBuildHistory] = useState<any[]>([]);
  const { toast } = useToast();

  const startFullBuild = async () => {
    setBuilding(true);
    setProgress({
      status: 'initializing',
      currentModule: 0,
      totalModules: 0,
      successCount: 0,
      failedCount: 0,
      errors: [],
      message: 'Initializing build...'
    });

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `https://wxgazidevakcnkrngykk.supabase.co/functions/v1/trigger-ssg-build`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ mode: 'full' }),
        }
      );

      if (!response.ok) throw new Error('Build failed to start');

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      while (reader) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = JSON.parse(line.slice(6));
            setProgress(data);

            if (data.status === 'completed') {
              toast({
                title: 'Build Completed',
                description: `${data.successCount} modules rendered successfully!`,
              });
              setBuildHistory(prev => [{
                timestamp: new Date().toISOString(),
                ...data
              }, ...prev]);
            }
          }
        }
      }
    } catch (error) {
      console.error('Build error:', error);
      toast({
        title: 'Build Failed',
        description: error.message,
        variant: 'destructive',
      });
      setProgress(prev => prev ? { ...prev, status: 'failed', message: error.message } : null);
    } finally {
      setBuilding(false);
    }
  };

  const startIncrementalBuild = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `https://wxgazidevakcnkrngykk.supabase.co/functions/v1/incremental-regen-trigger`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ hoursBack: 24 }),
        }
      );

      const result = await response.json();

      if (result.success) {
        toast({
          title: 'Incremental Build Completed',
          description: `${result.successCount}/${result.totalModules} modules updated`,
        });
      }
    } catch (error) {
      toast({
        title: 'Incremental Build Failed',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const progressPercentage = progress?.totalModules 
    ? (progress.currentModule / progress.totalModules) * 100 
    : 0;

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">SSG Builder</h1>
          <p className="text-muted-foreground">Pre-render modules for world-class performance</p>
        </div>
      </div>

      {/* Build Controls */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Build Controls</h2>
        <div className="flex gap-4">
          <Button
            onClick={startFullBuild}
            disabled={building}
            size="lg"
            className="flex-1"
          >
            {building ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Building...
              </>
            ) : (
              <>
                <PlayCircle className="mr-2 h-5 w-5" />
                Build All Modules (927)
              </>
            )}
          </Button>
          
          <Button
            onClick={startIncrementalBuild}
            disabled={building}
            variant="outline"
            size="lg"
            className="flex-1"
          >
            <RefreshCw className="mr-2 h-5 w-5" />
            Incremental Build (Last 24h)
          </Button>
        </div>
        <p className="text-sm text-muted-foreground mt-2">
          Full build: ~11-17 minutes | Incremental: ~30 seconds
        </p>
      </Card>

      {/* Build Progress */}
      {progress && (
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Build Progress</h2>
          
          <div className="space-y-4">
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm font-medium">
                  {progress.message}
                </span>
                <span className="text-sm text-muted-foreground">
                  {progress.currentModule}/{progress.totalModules}
                </span>
              </div>
              <Progress value={progressPercentage} />
            </div>

            {progress.currentCourse && (
              <Alert>
                <Clock className="h-4 w-4" />
                <AlertDescription>
                  Processing: {progress.currentCourse}
                </AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-3 gap-4">
              <Card className="p-4">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  <div>
                    <p className="text-sm text-muted-foreground">Success</p>
                    <p className="text-2xl font-bold">{progress.successCount}</p>
                  </div>
                </div>
              </Card>
              
              <Card className="p-4">
                <div className="flex items-center gap-2">
                  <XCircle className="h-5 w-5 text-red-500" />
                  <div>
                    <p className="text-sm text-muted-foreground">Failed</p>
                    <p className="text-2xl font-bold">{progress.failedCount}</p>
                  </div>
                </div>
              </Card>
              
              <Card className="p-4">
                <div className="flex items-center gap-2">
                  <Loader2 className="h-5 w-5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Status</p>
                    <p className="text-lg font-semibold capitalize">{progress.status}</p>
                  </div>
                </div>
              </Card>
            </div>

            {progress.errors.length > 0 && (
              <Alert variant="destructive">
                <AlertDescription>
                  <p className="font-semibold mb-2">Errors ({progress.errors.length}):</p>
                  <ul className="text-xs space-y-1 max-h-32 overflow-y-auto">
                    {progress.errors.slice(0, 10).map((error, i) => (
                      <li key={i}>• {error}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}
          </div>
        </Card>
      )}

      {/* Build History */}
      {buildHistory.length > 0 && (
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Build History</h2>
          <div className="space-y-2">
            {buildHistory.map((build, i) => (
              <div key={i} className="flex items-center justify-between p-3 border rounded">
                <div>
                  <p className="font-medium">
                    {new Date(build.timestamp).toLocaleString()}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {build.successCount} success, {build.failedCount} failed
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {build.status === 'completed' ? (
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
